import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../../../hooks/useChat';
import Icon from '../../../components/AppIcon';

import toast from 'react-hot-toast';

const SUGGESTED_QUESTIONS = [
  'What are the standout features of this property?',
  'How does the price compare to the neighborhood?',
  'What is the neighborhood like?',
  'Is this a good investment opportunity?',
  'What should I know before making an offer?',
  'Can I book an inspection?',
];

const buildSystemPrompt = (listing) => {
  const p = listing?.properties || {};
  const address = [p?.address, p?.suburb, p?.state, p?.postcode]?.filter(Boolean)?.join(', ');
  const price = listing?.price_or_rent
    ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 })?.format(listing?.price_or_rent)
    : 'Price on application';
  const listingType = listing?.listing_type === 'rent' ? 'For Rent' : 'For Sale';

  return `You are an expert AI property assistant for ListingLens, a real estate platform. You help buyers and renters understand property listings and make informed decisions.

You have full knowledge of the following property listing:

**Property Details:**
- Address: ${address || 'Not specified'}
- Listing Type: ${listingType}
- Price: ${price}${listing?.listing_type === 'rent' ? ' per week' : ''}
- Property Type: ${p?.type || 'Not specified'}
- Bedrooms: ${p?.beds || 'Not specified'}
- Bathrooms: ${p?.baths || 'Not specified'}
- Parking Spaces: ${p?.parking || 'Not specified'}
- Land Size: ${p?.land_size ? p?.land_size + ' sqm' : 'Not specified'}
- Floor Area: ${p?.floor_area ? p?.floor_area + ' sqm' : 'Not specified'}

**Description:**
${listing?.description_long || listing?.description_short || 'No description available.'}

**Neighborhood & Location:**
- Suburb: ${p?.suburb || 'Not specified'}
- State: ${p?.state || 'Not specified'}
- Postcode: ${p?.postcode || 'Not specified'}

**Your Role:**
- Answer questions about property features, layout, and condition
- Provide neighborhood insights (schools, transport, amenities, lifestyle)
- Discuss pricing context and investment potential
- Help with inspection scheduling and buyer concerns
- Offer honest, balanced advice about pros and cons
- Be concise, friendly, and professional
- If asked about specific data you don't have (e.g., exact school ratings), acknowledge the limitation and suggest how to find it
- For inspection bookings, let users know they can use the "Book Inspection" button on this page`;
};

const AIChatPanel = ({ listing }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isOpen, setIsOpen] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4.1', true);

  useEffect(() => {
    if (error) toast?.error('AI assistant error. Please try again.');
  }, [error]);

  // Track streaming response
  useEffect(() => {
    if (isLoading && response) {
      setStreamingMessage(response);
    }
  }, [response, isLoading]);

  // When streaming completes, add final message to history
  useEffect(() => {
    if (!isLoading && response && streamingMessage) {
      setMessages(prev => [
        ...prev,
        { id: Date.now(), role: 'assistant', content: response }
      ]);
      setStreamingMessage('');
    }
  }, [isLoading]);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  const handleSend = async (text) => {
    const messageText = text || inputMessage;
    if (!messageText?.trim() || isLoading) return;

    const userMsg = { id: Date.now(), role: 'user', content: messageText };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInputMessage('');

    const systemPrompt = buildSystemPrompt(listing);
    const apiMessages = [
      { role: 'system', content: systemPrompt },
      ...updatedMessages?.map(m => ({ role: m?.role, content: m?.content }))
    ];

    sendMessage(apiMessages, { max_completion_tokens: 600 });
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const hasConversation = messages?.length > 0 || isLoading;

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-primary text-primary-foreground p-4 flex items-center justify-between hover:bg-primary/90 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center">
            <Icon name="Bot" size={18} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-sm">AI Property Assistant</h3>
            <p className="text-xs text-primary-foreground/70">Ask anything about this property</p>
          </div>
        </div>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={18} />
      </button>
      {isOpen && (
        <>
          {/* Messages Area */}
          <div className="h-72 overflow-y-auto p-4 space-y-3 bg-background/50">
            {!hasConversation ? (
              <div className="flex flex-col items-center justify-center h-full text-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="MessageSquare" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Ask me about this property</p>
                  <p className="text-xs text-muted-foreground mt-1">Features, pricing, neighborhood, inspections & more</p>
                </div>
              </div>
            ) : (
              <>
                {messages?.map(msg => (
                  <div
                    key={msg?.id}
                    className={`flex ${msg?.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg?.role === 'assistant' && (
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                        <Icon name="Bot" size={12} color="var(--color-primary)" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
                        msg?.role === 'user' ?'bg-primary text-primary-foreground rounded-tr-sm' :'bg-muted text-foreground rounded-tl-sm'
                      }`}
                    >
                      {msg?.content}
                    </div>
                  </div>
                ))}

                {/* Streaming message */}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                      <Icon name="Bot" size={12} color="var(--color-primary)" />
                    </div>
                    <div className="max-w-[80%] rounded-xl rounded-tl-sm px-3 py-2 bg-muted text-foreground text-sm leading-relaxed">
                      {streamingMessage ? (
                        <span>{streamingMessage}<span className="inline-block w-1 h-3 bg-primary ml-0.5 animate-pulse rounded-sm" /></span>
                      ) : (
                        <div className="flex gap-1 items-center py-1">
                          <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Questions */}
          {!hasConversation && (
            <div className="px-4 pb-3">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Suggested questions</p>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTED_QUESTIONS?.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(q)}
                    disabled={isLoading}
                    className="text-xs bg-muted hover:bg-primary/10 hover:text-primary text-muted-foreground border border-border rounded-full px-2.5 py-1 transition-colors disabled:opacity-50"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-border bg-card">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                rows={1}
                value={inputMessage}
                onChange={(e) => setInputMessage(e?.target?.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about features, price, neighborhood..."
                disabled={isLoading}
                className="flex-1 resize-none text-sm bg-muted border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-50 min-h-[38px] max-h-[80px]"
                style={{ height: '38px' }}
                onInput={(e) => {
                  e.target.style.height = '38px';
                  e.target.style.height = Math.min(e?.target?.scrollHeight, 80) + 'px';
                }}
              />
              <button
                onClick={() => handleSend()}
                disabled={isLoading || !inputMessage?.trim()}
                className="w-9 h-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Icon name="Send" size={16} />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 text-center">Powered by AI · Responses may not be 100% accurate</p>
          </div>
        </>
      )}
    </div>
  );
};

export default AIChatPanel;